import { DEFAULT_CONTACT, MISSING_FIELD_LIST } from '@/entities/rma';
import { addTimezoneToMoment, convertToMoment, generateFullName } from '@/shared/lib';
import dayjs from 'dayjs';

/**
 * Seeds the create-return-shipping form from a study, pre-filling shipment date, patient sender address and facility recipient address. Uses the patient return study end date in the study timezone when it is still in the future and today otherwise, spreads DEFAULT_CONTACT under the patient and facility contacts while falling back from phone1 to phone2, and records SENDER_EMAIL, RECIPIENT_EMAIL and RECIPIENT_PHONE in missingModalData whenever those fields come out empty.
 *
 * @param study - Study whose patient, facility, timezone and return dates seed the form.
 * @returns Initial form state with the shipment date, both contacts and the list of missing required fields.
 */
const getDefaultState = (study: any) => {
  const studyEndDate = study?.patientReturn?.studyEndDate;
  const shipmentDate =
    studyEndDate && dayjs(studyEndDate).isAfter(dayjs(), 'day')
      ? convertToMoment({
          isoDate: studyEndDate,
          timezone: study?.timezone,
        })
      : addTimezoneToMoment({
          dateMoment: dayjs(),
          timezone: study?.timezone,
        });

  const patient = study?.info?.patient;
  const { contact: patientContact } = patient || {};

  const senderContact = {
    ...DEFAULT_CONTACT,
    name: generateFullName(patient),
    street1: patientContact?.address || '',
    city: patientContact?.city || '',
    country: patientContact?.country || '',
    state: patientContact?.state || '',
    zip: patientContact?.zip || '',
    phone: patientContact?.phone1 || patientContact?.phone2 || '',
    email: patient?.email || '',
  };

  const facility = study?.facility;
  const { contact: facilityContact } = facility || {};
  const recipientContact = {
    ...DEFAULT_CONTACT,
    name: facility?.name || '',
    street1: facilityContact?.address || '',
    city: facilityContact?.city || '',
    country: facilityContact?.country || '',
    state: facilityContact?.state || '',
    zip: facilityContact?.zip || '',
    phone: facilityContact?.phone1 || facilityContact?.phone2 || '',
  };

  const missingModalData = [];
  if (!senderContact.email) {
    missingModalData.push(MISSING_FIELD_LIST.SENDER_EMAIL);
  }
  if (!recipientContact.email) {
    missingModalData.push(MISSING_FIELD_LIST.RECIPIENT_EMAIL);
  }

  if (!recipientContact.phone) {
    missingModalData.push(MISSING_FIELD_LIST.RECIPIENT_PHONE);
  }

  return {
    shipmentDate,
    senderContact,
    recipientContact,
    missingModalData,
  };
};

export { getDefaultState };
